import React, { useEffect, useMemo, useState } from 'react';
import { fetchCities } from '../../services/Http/Api';
import { fetchAdminCities } from '../../services/Http/AdminApi';
import { SearchableSelect } from '../../shared/layout/forms/AppSelect';

const CitySearchSelect = ({
    countryId,
    value,
    cityId,
    onChange,
    placeholder = 'Select city',
    useAdminApi = false,
    isDisabled = false,
    isClearable = true,
}) => {
    const [cities, setCities] = useState([]);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        let active = true;

        const loadCities = async () => {
            try {
                setLoading(true);
                const params = { country_id: countryId || undefined, per_page: 500 };
                const response = useAdminApi
                    ? await fetchAdminCities(params)
                    : await fetchCities(params);
                const list = Array.isArray(response) ? response : response?.data || [];
                if (!active) return;
                setCities(
                    list.map((city) => ({
                        id: city.id,
                        name: city.name,
                        country_id: city.country_id,
                    }))
                );
            } catch (error) {
                if (active) {
                    setCities([]);
                }
            } finally {
                if (active) {
                    setLoading(false);
                }
            }
        };

        loadCities();
        return () => {
            active = false;
        };
    }, [countryId, useAdminApi]);

    const options = useMemo(
        () =>
            cities.map((city) => ({
                value: city.id,
                label: city.name,
                city,
            })),
        [cities]
    );

    const selectedOption = useMemo(() => {
        if (cityId) {
            const byId = options.find((option) => Number(option.value) === Number(cityId));
            if (byId) return byId;
        }
        if (!value) return null;
        return options.find((option) => option.label === value) || null;
    }, [cityId, options, value]);

    return (
        <SearchableSelect
            isSearchable
            isClearable={isClearable}
            isDisabled={isDisabled}
            isLoading={loading}
            placeholder={placeholder}
            options={options}
            value={selectedOption}
            onChange={(_, __, option) => onChange(option?.city || null)}
            styles={selectStyles}
        />
    );
};

const selectStyles = {
    control: (base, state) => ({
        ...base,
        borderColor: state.isFocused ? '#821692' : '#e5e7eb',
        borderWidth: '1.5px',
        boxShadow: state.isFocused ? '0 0 0 4px rgba(130, 22, 146, 0.1)' : 'none',
        backgroundColor: '#ffffff',
        borderRadius: '10px',
        minHeight: '46px',
        '&:hover': { borderColor: state.isFocused ? '#821692' : '#d1d5db' },
    }),
    option: (base, state) => ({
        ...base,
        backgroundColor: state.isSelected
            ? '#821692'
            : state.isFocused
                ? 'rgba(130, 22, 146, 0.08)'
                : '#fff',
        color: state.isSelected ? '#fff' : '#1f2937',
        cursor: 'pointer',
        padding: '0.625rem 0.875rem',
        fontSize: '0.95rem',
    }),
    singleValue: (base) => ({
        ...base,
        color: '#1f2937',
        fontSize: '0.95rem',
    }),
    placeholder: (base) => ({
        ...base,
        color: '#9ca3af',
        fontSize: '0.95rem',
    }),
};

export default CitySearchSelect;
